import {
  BellRing,
  Building2,
  Globe,
  ListChecks,
  Plug,
  Radar,
  Settings2,
  SlidersHorizontal,
  Swords,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { UiV2NavItem, UiV2NavSection } from '@/components/UiV2/UiV2Shell';
import {
  POSTURE_MANAGEMENT_TAB_META,
  type PostureManagementTabGates,
} from '@/components/Inventory/tabs';
import {
  normalizeTabKey,
  type OrgTabKey,
} from '@/components/settings/settingsTabs';
import { trackEvent } from '@/lib/analytics';
import {
  AI_AGENT_INSTRUCTIONS_SLUG,
  POLICIES_AI_INSTRUCTIONS_SLUG,
  POLICIES_ALLOW_BLOCK_SLUG,
  POLICIES_POLICY_SETTINGS_SLUG,
} from '@/lib/policyPaths';
import { OrgTab, type OrgTabType } from './orgTabKeys';
import type { OrgTabConfig } from './constants';

interface BuildUiV2NavOptions {
  organizationId: string;
  /** Current pathname, trailing slashes stripped. */
  pathname: string;
  /** The `?tab=` value — Inventory and Settings both select sub-tabs with it. */
  tabParam: string | null;
  currentTab: OrgTabType;
  tabConfig: Partial<OrgTabConfig>;
  isTabVisible: (tab: OrgTabType) => boolean;
  canViewAdminContent: boolean;
  /** The same gates OrganizationView hands PostureManagement. */
  inventoryTabGates: PostureManagementTabGates;
  networkDiscoveryEnabled: boolean;
  browserAgentSitesEnabled: boolean;
  aiAgentInstructionsEnabled: boolean;
  /** `useOrgPolicySettingsAccess().canAccess` (KIR-5106). */
  policySettingsVisible: boolean;
}

/**
 * Sidebar for the opt-in UI v2 (kirin_ui.html). Same routes, labels and icons
 * as the classic sidebar (TAB_CONFIG), regrouped into the mockup's sections.
 * Inventory and Settings sub-tabs become their own items. Dashboard sits on
 * top in an untitled section. Pages the mockup predates go to the closest section: Projects,
 * Hooks, and Packages & Repos under Discovery & Inventory; AI Agent
 * Instructions under Posture; Attack Demonstrations under Threat Detection;
 * Security Groups and Get Started under Settings. AI AppSec stays its own
 * section. Settings is last because the shell pins the last section to the
 * bottom.
 */
export function buildUiV2Nav(o: BuildUiV2NavOptions): UiV2NavSection[] {
  const base = `/${o.organizationId}`;
  const sub = o.pathname.slice(base.length);
  const under = (path: string): boolean =>
    sub === path || sub.startsWith(`${path}/`);
  const inventoryTabs = Object.entries(POSTURE_MANAGEMENT_TAB_META).map(
    ([key, meta]) => ({ key, meta, visible: meta.visible(o.inventoryTabGates) })
  );
  // Deep pages (/inventory/projects/:slug, /inventory/extensions/:id/scan-result)
  // keep their Inventory sub-tab lit. With no visible ?tab= the first visible
  // tab is lit — the one useInventoryTabManagement is about to write to the URL.
  const inventoryKey =
    [sub.split('/')[2], o.tabParam].find(key =>
      inventoryTabs.some(t => t.visible && t.key === key)
    ) ?? inventoryTabs.find(t => t.visible)?.key;
  const isAiInstructionsPolicy =
    under(`/policies/${POLICIES_AI_INSTRUCTIONS_SLUG}`) ||
    under(`/policies/${AI_AGENT_INSTRUCTIONS_SLUG}`);
  const isAllowBlock = under(`/policies/${POLICIES_ALLOW_BLOCK_SLUG}`);
  const isPolicySettings = under(`/policies/${POLICIES_POLICY_SETTINGS_SLUG}`);
  const isNetworkDiscovery = under('/shadow-ai/network-discovery');
  const isBrowserAgentSites = under('/agent-activity/browser-agent-sites');
  // Same fallback as SettingsTabBar: a missing or unknown ?tab= is Organization.
  const settingsTab = normalizeTabKey('organization', o.tabParam);

  type Item = UiV2NavItem & { visible: boolean };
  interface ItemOverrides {
    /** Sub-pages set their own; a plain tab takes TAB_CONFIG's. */
    label?: string;
    icon?: LucideIcon;
    path?: string;
    active?: boolean;
    visible?: boolean;
    onNavigate?: () => void;
  }
  const item = (tab: OrgTabType, extra: ItemOverrides = {}): Item | null => {
    const config = o.tabConfig[tab];
    // isTabVisible is false for a tab the config doesn't carry.
    if (!config) return null;
    return {
      label: extra.label ?? config.label,
      icon: extra.icon ?? config.icon,
      to: `${base}/${extra.path ?? tab}`,
      active: o.currentTab === tab && (extra.active ?? true),
      roles: config.roles,
      visible: o.isTabVisible(tab) && (extra.visible ?? true),
      onNavigate: extra.onNavigate,
    };
  };
  // One item per Inventory tab, same order and gates as the tab bar.
  const inventoryItems = inventoryTabs.map(({ key, meta, visible }) =>
    item(OrgTab.PostureManagement, {
      label: meta.label,
      icon: meta.icon,
      path: `${OrgTab.PostureManagement}?tab=${key}`,
      active: inventoryKey === key,
      visible,
    })
  );
  const settings = (
    key: OrgTabKey,
    label: string,
    icon: LucideIcon,
    visible = true
  ): Item | null =>
    item(OrgTab.Settings, {
      label,
      icon,
      path: `${OrgTab.Settings}?tab=${key}`,
      active: settingsTab === key,
      visible,
      // The Settings tab bar is hidden under the shell, so its "sub-tab
      // opened" event is reported from here instead.
      onNavigate: () =>
        trackEvent('settings_tab_opened', {
          tab: key,
          context: 'organization',
        }),
    });

  const sections: { title?: string; items: (Item | null)[] }[] = [
    { items: [item(OrgTab.Dashboard)] },
    {
      title: 'Discovery & Inventory',
      items: [
        ...inventoryItems,
        item(OrgTab.ShadowAI, { active: !isNetworkDiscovery }),
        item(OrgTab.ShadowAI, {
          label: 'Network Discovery',
          icon: Radar,
          path: 'shadow-ai/network-discovery',
          active: isNetworkDiscovery,
          visible: o.networkDiscoveryEnabled,
        }),
        item(OrgTab.AgentsActivity, { active: !isBrowserAgentSites }),
        item(OrgTab.AgentsActivity, {
          label: 'Browser Agent Sites',
          icon: Globe,
          path: 'agent-activity/browser-agent-sites',
          active: isBrowserAgentSites,
          visible: o.browserAgentSitesEnabled,
        }),
      ],
    },
    {
      title: 'Security Posture Management',
      items: [
        item(OrgTab.Policies, {
          label: 'AI Agent Instructions',
          icon: Workflow,
          path: `policies/${POLICIES_AI_INSTRUCTIONS_SLUG}`,
          active: isAiInstructionsPolicy,
          visible: o.aiAgentInstructionsEnabled,
        }),
        item(OrgTab.Policies, {
          label: 'Allow/Block Lists',
          icon: ListChecks,
          path: `policies/${POLICIES_ALLOW_BLOCK_SLUG}`,
          active: isAllowBlock,
        }),
        item(OrgTab.Policies, {
          label: 'Policy Settings',
          icon: Settings2,
          path: `policies/${POLICIES_POLICY_SETTINGS_SLUG}`,
          active: isPolicySettings,
          visible: o.policySettingsVisible,
        }),
      ],
    },
    {
      title: 'AI AppSec',
      items: [
        item(OrgTab.SecureAiCoding),
        // Classic shows all three under one icon; siblings get their own here.
        item(OrgTab.ThreatModeling, { icon: Swords }),
        item(OrgTab.LlmVulnDiscovery, { icon: Radar }),
      ],
    },
    {
      title: 'Threat Detection & Response',
      items: [
        item(OrgTab.Alerts),
        item(OrgTab.Policies, {
          label: 'Detection Policies',
          active: !isAiInstructionsPolicy && !isAllowBlock && !isPolicySettings,
        }),
        item(OrgTab.AttackDemonstrations),
      ],
    },
    {
      title: 'Settings',
      items: [
        // Not the config label: "Users" would sit in the same sidebar as the
        // Inventory → Users item.
        item(OrgTab.Users, { label: 'Users & Roles' }),
        item(OrgTab.SecurityGroups),
        settings('organization', 'Organization', Building2),
        // The Settings page hides Integrations from non-admins too.
        settings('integrations', 'Integrations', Plug, o.canViewAdminContent),
        settings('notifications', 'Notifications', BellRing),
        settings('preferences', 'Preferences', SlidersHorizontal),
        item(OrgTab.AuditLogs),
        item(OrgTab.Install),
      ],
    },
  ];

  return sections
    .map(s => ({
      ...s,
      items: s.items.filter((i): i is Item => i !== null && i.visible),
    }))
    .filter(s => s.items.length > 0);
}
